import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar as CalendarIcon, MapPin, Users, List, Grid, Plus, Crown, DollarSign, Search, X, Globe } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { format, parseISO, isSameDay } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Events = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
    fetchCategories();
    fetchRegions();
  }, [token, selectedRegion]);

  useEffect(() => {
    filterEvents();
  }, [events, selectedCategory, selectedDate, searchQuery]);

  const fetchEvents = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = {};
      if (selectedRegion && selectedRegion !== 'all') {
        params.region = selectedRegion;
      }
      const response = await axios.get(`${API}/events`, { headers, params });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axios.get(`${API}/regions`);
      setRegions(response.data);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const filterEvents = () => {
    let filtered = [...events];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(e => 
        e.title.toLowerCase().includes(query) ||
        e.description.toLowerCase().includes(query) ||
        e.location.toLowerCase().includes(query) ||
        e.organizer_name?.toLowerCase().includes(query)
      );
    }
    
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(e => e.category === selectedCategory);
    }
    
    if (selectedDate) {
      filtered = filtered.filter(e => isSameDay(parseISO(e.date), selectedDate));
    }
    
    setFilteredEvents(filtered);
  };

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const eventDates = events.map(e => parseISO(e.date));

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedRegion('all');
    setSelectedDate(undefined);
    setSearchQuery('');
  };

  const hasActiveFilters = selectedCategory !== 'all' || selectedRegion !== 'all' || selectedDate || searchQuery.trim();

  const getRegionLabel = (value) => {
    const region = regions.find(r => r.value === value);
    return region ? region.label : value;
  };

  return (
    <div className="min-h-screen py-12">
      <div className="container-custom max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8">
          <div>
            <p className="overline text-gold mb-4">Discover</p>
            <h1 className="text-4xl md:text-5xl">Events</h1>
          </div>
          {user && (
            <Button 
              onClick={() => navigate('/create-event')}
              className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-6 py-3 text-sm font-semibold rounded-sm mt-6 md:mt-0"
              data-testid="create-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Event
            </Button>
          )}
        </div>

        {/* Search Bar */}
        <div className="mb-10">
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search events by title, description, location, or organizer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-10 py-6 text-base rounded-sm border-border/50 focus:border-gold"
              data-testid="event-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="clear-search-btn"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-10">
          {/* Sidebar with Calendar and Filters */}
          <div className="lg:col-span-1 space-y-8">
            {/* Calendar */}
            <div className="border border-border/50 p-4 bg-card">
              <h3 className="text-lg mb-4 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-gold" />
                Calendar
              </h3>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-none"
                modifiers={{
                  hasEvent: eventDates,
                }}
                modifiersStyles={{
                  hasEvent: {
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(198, 168, 124, 0.2)',
                  },
                }}
                data-testid="event-calendar"
              />
            </div>

            {/* Category Filter */}
            <div className="border border-border/50 p-4 bg-card">
              <h3 className="text-lg mb-4">Filter by Category</h3>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full rounded-sm" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Region Filter */}
            <div className="border border-border/50 p-4 bg-card">
              <h3 className="text-lg mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-gold" />
                Filter by Region
              </h3>
              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                <SelectTrigger className="w-full rounded-sm" data-testid="region-filter">
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {regions.map((region) => (
                    <SelectItem key={region.value} value={region.value}>
                      {region.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button 
                variant="outline" 
                onClick={clearFilters}
                className="w-full rounded-sm uppercase tracking-widest text-sm"
                data-testid="clear-filters-btn"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Events List */}
          <div className="lg:col-span-3">
            {/* View Toggle */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-muted-foreground">
                {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
                {searchQuery && ` for "${searchQuery}"`}
                {selectedDate && ` on ${format(selectedDate, 'MMM d, yyyy')}`}
              </p>
              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                  className="rounded-sm"
                  data-testid="grid-view-btn"
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                  className="rounded-sm"
                  data-testid="list-view-btn"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="spinner" />
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="text-center py-20 border border-border/50 bg-card">
                <CalendarIcon className="w-12 h-12 text-gold mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-xl mb-2">No Events Found</h3>
                <p className="text-muted-foreground mb-6">
                  {selectedDate || selectedCategory !== 'all' 
                    ? 'Try adjusting your filters to find more events.'
                    : 'Be the first to create an event for the community.'}
                </p>
                {user && (
                  <Button 
                    onClick={() => navigate('/create-event')}
                    className="bg-gold text-primary hover:bg-gold/90 uppercase tracking-widest px-6 py-3 text-sm font-semibold rounded-sm"
                    data-testid="create-event-empty-btn"
                  >
                    Create Event
                  </Button>
                )}
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid md:grid-cols-2 gap-6">
                {filteredEvents.map((event, index) => (
                  <Link
                    key={event.id}
                    to={`/events/${event.id}`}
                    className="group card-hover border border-border/50 bg-card"
                    data-testid={`event-grid-item-${index}`}
                  >
                    <div className="overflow-hidden relative">
                      <img
                        src={event.image_url && !event.image_url.includes('example.com') ? event.image_url : 'https://images.pexels.com/photos/8761648/pexels-photo-8761648.jpeg'}
                        alt={event.title}
                        className="event-card-image w-full transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => { e.target.src = 'https://images.pexels.com/photos/8761648/pexels-photo-8761648.jpeg'; }}
                      />
                      {event.is_paid && (
                        <span className="absolute top-3 right-3 bg-gold text-primary text-xs px-2 py-1 uppercase tracking-wider flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />${event.ticket_price}
                        </span>
                      )}
                      {event.is_featured && (
                        <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs px-2 py-1 uppercase tracking-wider flex items-center gap-1">
                          <Crown className="w-3 h-3" /> Featured
                        </span>
                      )}
                    </div>
                    <div className="p-6">
                      <span className="badge-category mb-4 inline-block">
                        {getCategoryLabel(event.category)}
                      </span>
                      <h3 className="text-xl mb-2 group-hover:text-gold transition-colors line-clamp-2">
                        {event.title}
                      </h3>
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                        {event.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {format(parseISO(event.date), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {event.rsvp_count}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredEvents.map((event, index) => (
                  <Link
                    key={event.id}
                    to={`/events/${event.id}`}
                    className="group card-hover border border-border/50 bg-card flex gap-6 p-4"
                    data-testid={`event-list-item-${index}`}
                  >
                    <div className="w-48 flex-shrink-0 overflow-hidden relative">
                      <img
                        src={event.image_url && !event.image_url.includes('example.com') ? event.image_url : 'https://images.pexels.com/photos/8761648/pexels-photo-8761648.jpeg'}
                        alt={event.title}
                        className="w-full h-32 object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => { e.target.src = 'https://images.pexels.com/photos/8761648/pexels-photo-8761648.jpeg'; }}
                      />
                      {event.is_paid && (
                        <span className="absolute top-2 right-2 bg-gold text-primary text-xs px-2 py-1">
                          ${event.ticket_price}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 py-2">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="badge-category">
                          {getCategoryLabel(event.category)}
                        </span>
                        {event.is_featured && (
                          <span className="inline-flex items-center gap-1 text-xs text-gold">
                            <Crown className="w-3 h-3" /> Featured
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl mb-1 group-hover:text-gold transition-colors">
                        {event.title}
                      </h3>
                      <p className="text-muted-foreground text-sm line-clamp-1 mb-3">
                        {event.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {format(parseISO(event.date), 'MMM d, yyyy')} • {event.start_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {event.rsvp_count}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Events;
